/**
 * RUBRIKER, BRICKOR OCH VÄLJARE — de små sakerna högst upp på en skärm.
 */

import type { ReactNode } from 'react';

/* == Rubrik ============================================================== */

export interface Rubrikprops {
  /** `.h-adr` — stor, --display. På en arbetsorder är det ADRESSEN. */
  readonly titel: ReactNode;
  /** `.h-sub` — dämpad rad under. Postort, ordernummer, kundtyp. */
  readonly under?: ReactNode;
}

export const Rubrik = ({ titel, under }: Rubrikprops) => (
  <>
    <p className="h-adr">{titel}</p>
    {under !== undefined && <p className="h-sub">{under}</p>}
  </>
);

/**
 * `.daterow` — startsidornas rad: "**Torsdag 3 september** · Anton Ek".
 * Det halvfeta står i `stark`.
 */
export const Datumrad = ({
  stark,
  children,
}: {
  readonly stark: ReactNode;
  readonly children?: ReactNode;
}) => (
  <p className="daterow">
    <b>{stark}</b>
    {children !== undefined && <> · {children}</>}
  </p>
);

/** `.eyebrow` — liten versal etikett i --mono över en rubrik. */
export const Overrubrik = ({ children }: { readonly children: ReactNode }) => (
  <p className="eyebrow">{children}</p>
);

/* == Brickan ============================================================= */

/**
 * `.chip`. FYRA TONER, och de betyder saker:
 *   'tilldelad'  --accent   något väntar på dig
 *   'pagaende'   --warn     något är igång eller ofullständigt
 *   'klar'       --good     något är gjort
 *   'neutral'    --muted    en ren etikett utan värde
 *
 * BRICKAN ÄR ALDRIG ETT KORT. --accent-quiet är en liten fylld yta på några
 * kvadratcentimeter; ägaren har sagt nej till rosa fyllningar på kort, och det
 * gäller inte den här.
 */
export type Brickton = 'tilldelad' | 'pagaende' | 'klar' | 'neutral';

const BRICKKLASS: Record<Brickton, string> = {
  tilldelad: 'chip till',
  pagaende: 'chip pag',
  klar: 'chip klar',
  neutral: 'chip k-neutral',
};

export const Bricka = ({
  ton = 'neutral',
  children,
}: {
  readonly ton?: Brickton;
  readonly children: ReactNode;
}) => <span className={BRICKKLASS[ton]}>{children}</span>;

/** `.krav` — "Privatkund — moment krävs". En bricka i --mono. */
export const Kravbricka = ({ children }: { readonly children: ReactNode }) => (
  <span className="krav">{children}</span>
);

/* == Segmentväljaren ===================================================== */

export interface Segmentval<T extends string> {
  readonly varde: T;
  readonly etikett: ReactNode;
}

export interface Segmentprops<T extends string> {
  readonly val: readonly Segmentval<T>[];
  readonly valt: T;
  readonly onValj: (varde: T) => void;
  /** Skärmläsarens namn på gruppen: "Filter", "Period". */
  readonly etikett: string;
}

/**
 * `.segs` — filterremsan (Idag / Veckan / Alla).
 *
 * DEN FILTRERAR I KLIENTEN. shared/routes.ts säger det uttryckligen om
 * arbetsordrarna: "'Mina arbetsordrar' är en SORTERING i klienten, inte ett
 * filter." Servern skickar allt montören får se, och remsan väljer ur det.
 */
export const Segment = <T extends string>({
  val,
  valt,
  onValj,
  etikett,
}: Segmentprops<T>) => (
  <div className="segs" role="group" aria-label={etikett}>
    {val.map((v) => (
      <button
        key={v.varde}
        type="button"
        className="seg"
        aria-pressed={v.varde === valt}
        onClick={() => onValj(v.varde)}
      >
        {v.etikett}
      </button>
    ))}
  </div>
);
